/**
 * salesTrend.ts — Pure computation of a sales time series with two comparison
 * overlays: the immediately-preceding period and the same period a year ago.
 *
 * The current Sales report's only chart is a 24-bar hourly histogram, which over
 * a month flattens every day into the same 24 buckets and answers no "are we
 * growing?" question. This bins net sales by day / week / month across the range
 * and lines the two comparison periods up against it.
 *
 * Alignment trick: rather than bucket the comparison periods on their own
 * calendars and try to pair them up, their invoice dates are *shifted forward*
 * onto the current range — the previous period by the range's own span, last
 * year by one year — and then bucketed with the exact same scheme. Bucket i of
 * every series therefore lines up by construction, for any granularity.
 *
 * Net of returns: a credit note (sign −1) subtracts, matching the Sales report's
 * `totalSales`. Pure — see `salesTrendDataSource.ts` for the WatermelonDB bridge.
 */

export type Granularity = 'day' | 'week' | 'month';

export const CREDIT_NOTE = 'CREDIT_NOTE';

const DAY_MS = 24 * 60 * 60 * 1000;

export function round2(n: number): number {
  return Math.round((n + Number.EPSILON) * 100) / 100;
}

function signOf(type: string): 1 | -1 {
  return type === CREDIT_NOTE ? -1 : 1;
}

// ─── Inputs ──────────────────────────────────────────────────────────────────

export interface TrendInvoiceInput {
  type: string;
  totalAmount: number;
  date: Date;
}

export interface SalesTrendInput {
  current: TrendInvoiceInput[];
  /** Invoices from the immediately-preceding equal-length window. */
  previous: TrendInvoiceInput[];
  /** Invoices from the same window one year earlier. */
  lastYear: TrendInvoiceInput[];
  from: Date;
  /** Exclusive. */
  to: Date;
  granularity: Granularity;
}

// ─── Output ──────────────────────────────────────────────────────────────────

export interface TrendBucket {
  /** 0-based position in the series. */
  index: number;
  /** Start of this bucket in the current period — for labelling. */
  start: Date;
  current: number;
  previous: number;
  lastYear: number;
}

export interface SalesTrend {
  buckets: TrendBucket[];
  granularity: Granularity;
  totals: { current: number; previous: number; lastYear: number };
  /** (current − previous) / previous; 0 when previous is 0. */
  deltaPrevPct: number;
  /** (current − lastYear) / lastYear; 0 when lastYear is 0. */
  deltaLastYearPct: number;
  hasPrevious: boolean;
  hasLastYear: boolean;
}

// ─── Bucketing ────────────────────────────────────────────────────────────────

function monthStart(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}

function addMonths(d: Date, n: number): Date {
  return new Date(d.getFullYear(), d.getMonth() + n, 1);
}

/** Bucket boundaries of the current period, plus the count. */
function planBuckets(from: Date, to: Date, granularity: Granularity): Date[] {
  const starts: Date[] = [];
  if (granularity === 'month') {
    const first = monthStart(from);
    const last = monthStart(new Date(to.getTime() - 1)); // last day actually covered
    for (let m = new Date(first); m <= last; m = addMonths(m, 1)) starts.push(new Date(m));
    return starts;
  }
  const step = granularity === 'week' ? 7 * DAY_MS : DAY_MS;
  const spanDays = Math.max(1, Math.ceil((to.getTime() - from.getTime()) / DAY_MS));
  const count = granularity === 'week' ? Math.ceil(spanDays / 7) : spanDays;
  for (let i = 0; i < count; i++) starts.push(new Date(from.getTime() + i * step));
  return starts;
}

/** Which bucket a date lands in, relative to `from`, or -1 if before it. */
function bucketIndex(d: Date, from: Date, granularity: Granularity): number {
  if (granularity === 'month') {
    return (d.getFullYear() - from.getFullYear()) * 12 + (d.getMonth() - from.getMonth());
  }
  const diff = d.getTime() - from.getTime();
  if (diff < 0) return -1;
  const unit = granularity === 'week' ? 7 * DAY_MS : DAY_MS;
  return Math.floor(diff / unit);
}

export function buildSalesTrend(input: SalesTrendInput): SalesTrend {
  const { current, previous, lastYear, from, to, granularity } = input;

  const starts = planBuckets(from, to, granularity);
  const n = starts.length;
  const cur = new Array(n).fill(0);
  const prev = new Array(n).fill(0);
  const ly = new Array(n).fill(0);

  const span = to.getTime() - from.getTime();

  const accumulate = (rows: TrendInvoiceInput[], target: number[], shiftMs: number, shiftYears: number) => {
    for (const inv of rows) {
      const shifted = shiftYears
        ? new Date(inv.date.getFullYear() + shiftYears, inv.date.getMonth(), inv.date.getDate(),
            inv.date.getHours(), inv.date.getMinutes(), inv.date.getSeconds())
        : new Date(inv.date.getTime() + shiftMs);
      const idx = bucketIndex(shifted, from, granularity);
      if (idx >= 0 && idx < n) target[idx] += signOf(inv.type) * inv.totalAmount;
    }
  };

  accumulate(current, cur, 0, 0);
  accumulate(previous, prev, span, 0); // shift forward by one span onto the current range
  accumulate(lastYear, ly, 0, 1); // shift forward by one year

  const buckets: TrendBucket[] = starts.map((start, i) => ({
    index: i,
    start,
    current: round2(cur[i]),
    previous: round2(prev[i]),
    lastYear: round2(ly[i]),
  }));

  const sum = (a: number[]) => round2(a.reduce((s, v) => s + v, 0));
  const totals = { current: sum(cur), previous: sum(prev), lastYear: sum(ly) };
  const pctDelta = (now: number, base: number) => (base !== 0 ? (now - base) / base : 0);

  return {
    buckets,
    granularity,
    totals,
    deltaPrevPct: pctDelta(totals.current, totals.previous),
    deltaLastYearPct: pctDelta(totals.current, totals.lastYear),
    hasPrevious: previous.length > 0,
    hasLastYear: lastYear.length > 0,
  };
}
